import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SPLIT_BREAKPOINT } from '../domain/constants';

// Divisorio trascinabile fra pannello sinistro e destro.
//
// Usa Pointer Events (non Mouse Events: prima era inutilizzabile su tablet),
// ascolta su window invece che sul contenitore (un trascinamento veloce fuori
// dall'elemento perdeva il gesto), e persiste la larghezza in percentuale, non
// in pixel, cosi cambiare risoluzione non lascia un pannello sproporzionato.
//
// Sotto la soglia i pannelli si impilano e NESSUNA larghezza inline viene
// applicata: uno stile inline batte qualunque regola della media query, ed era
// il motivo per cui il layout restava a due colonne strettissime sul telefono.
// La soglia deve combaciare con `@media (min-width: 64rem)` in layout.css.

const MIN_PERCENT = 25;
const MAX_PERCENT = 75;

function useIsSplit() {
    const query = `(min-width: ${SPLIT_BREAKPOINT}px)`;
    const [isSplit, setIsSplit] = useState(
        () => typeof window !== 'undefined' && window.matchMedia?.(query).matches === true,
    );

    useEffect(() => {
        const media = window.matchMedia(query);
        const update = (event) => setIsSplit(event.matches);
        setIsSplit(media.matches);
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, [query]);

    return isSplit;
}

export default function SplitPane({ storageKey = 'astafanta:split', left, right }) {
    const containerRef = useRef(null);
    const isSplit = useIsSplit();
    const [dragging, setDragging] = useState(false);
    const [width, setWidth] = useState(() => {
        const saved = Number.parseFloat(window.localStorage?.getItem(storageKey));
        return Number.isFinite(saved) ? Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, saved)) : 50;
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(storageKey, String(width));
        } catch {
            /* storage non disponibile */
        }
    }, [width, storageKey]);

    const onPointerMove = useCallback((event) => {
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const percent = ((event.clientX - rect.left) / rect.width) * 100;
        setWidth(Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, percent)));
    }, []);

    const stopDragging = useCallback(() => setDragging(false), []);

    useEffect(() => {
        if (!dragging) return undefined;
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', stopDragging);
        window.addEventListener('pointercancel', stopDragging);
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', stopDragging);
            window.removeEventListener('pointercancel', stopDragging);
        };
    }, [dragging, onPointerMove, stopDragging]);

    // La maniglia si sposta anche da tastiera: e' un `separator` con valore, e
    // trascinare col mouse non puo' essere l'unico modo di usarla.
    const nudge = useCallback((delta) => {
        setWidth((current) => Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, current + delta)));
    }, []);

    // La maniglia occupa spazio anche lei: se i due pannelli si spartiscono il
    // 100% pieno, la somma sfora e la pagina guadagna una barra di scorrimento
    // orizzontale. Ognuno cede meta della larghezza della maniglia.
    const half = 'var(--split-handle-w) / 2';
    const leftStyle = isSplit ? { width: `calc(${width}% - ${half})`, flex: 'none' } : undefined;
    const rightStyle = isSplit
        ? { width: `calc(${100 - width}% - ${half})`, flex: 'none' }
        : undefined;

    return (
        <div className="split" ref={containerRef}>
            <div className="split-panel" style={leftStyle}>
                {left}
            </div>
            {isSplit && (
                <div
                    className="split-handle"
                    onPointerDown={(event) => {
                        event.preventDefault();
                        setDragging(true);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'ArrowLeft') nudge(-5);
                        else if (event.key === 'ArrowRight') nudge(5);
                        else return;
                        event.preventDefault();
                    }}
                    role="separator"
                    tabIndex={0}
                    aria-orientation="vertical"
                    aria-label="Ridimensiona i pannelli"
                    aria-valuenow={Math.round(width)}
                    aria-valuemin={MIN_PERCENT}
                    aria-valuemax={MAX_PERCENT}
                >
                    <div className="split-handle-bar" />
                </div>
            )}
            <div className="split-panel" style={rightStyle}>
                {right}
            </div>
        </div>
    );
}
